import { useState, useEffect } from 'react';
import { useInAppBrowser, getBrowserName } from '@/hooks/useInAppBrowser';
import { ExternalLink, X, Smartphone, AlertTriangle, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface InAppBrowserBannerProps {
  /** Only show on critical pages like auth */
  variant?: 'minimal' | 'prominent';
}

export const InAppBrowserBanner = ({ variant = 'prominent' }: InAppBrowserBannerProps) => {
  const { isInAppBrowser, browserType, platform, canRedirect, openInExternalBrowser } = useInAppBrowser();
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  // Check if previously dismissed in this session
  useEffect(() => {
    const wasDismissed = sessionStorage.getItem('inapp_banner_dismissed');
    if (wasDismissed) {
      setDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('inapp_banner_dismissed', 'true');
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = window.location.href;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenBrowser = () => {
    if (canRedirect) {
      openInExternalBrowser();
    } else {
      setShowInstructions(true);
    }
  };

  // Don't render if not in-app browser or dismissed
  if (!isInAppBrowser || dismissed) {
    return null;
  }

  const browserName = getBrowserName(browserType);

  // iOS instructions overlay
  if (showInstructions && platform === 'ios') {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 animate-fade-in">
        <div className="bg-card rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-scale-in">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-cyan/20 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-cyan" />
              </div>
              <h3 className="font-bold text-lg">Open in Safari</h3>
            </div>
            <button 
              onClick={() => setShowInstructions(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4 mb-6">
            <p className="text-sm text-muted-foreground">
              Voor de beste ervaring, open deze pagina in Safari:
            </p>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-xl">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-cyan text-white text-xs font-bold shrink-0">1</span>
                <p className="text-sm">Tik rechtsonder op <strong>⋯</strong> (drie puntjes) of het <strong>deel icoon</strong></p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-xl">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-cyan text-white text-xs font-bold shrink-0">2</span>
                <p className="text-sm">Kies <strong>"Openen in Safari"</strong> of <strong>"Open in browser"</strong></p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Button 
              onClick={handleCopyUrl} 
              variant="outline" 
              className="w-full gap-2"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  Link gekopieerd!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Of kopieer de link
                </>
              )}
            </Button>
            <Button 
              onClick={() => setShowInstructions(false)} 
              variant="ghost" 
              className="w-full"
            >
              Toch doorgaan
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Minimal variant - just a small indicator
  if (variant === 'minimal') {
    return (
      <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-3 mb-4 flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
        <p className="text-xs text-amber-800 dark:text-amber-200 flex-1">
          Je gebruikt de {browserName} browser. <button onClick={handleOpenBrowser} className="underline font-medium">Open in je browser</button> voor de beste ervaring.
        </p>
        <button onClick={handleDismiss} className="text-amber-600 hover:text-amber-800">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Prominent variant - clear call to action
  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 border-2 border-amber-200 dark:border-amber-700 rounded-2xl p-4 mb-4 animate-fade-in">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center shrink-0">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-sm text-amber-900 dark:text-amber-100 mb-1">
            {browserName} browser gedetecteerd
          </h4>
          <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
            Voor een vlotte registratie, open deze pagina in je standaard browser. Je sessie blijft dan behouden.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button 
              size="sm" 
              onClick={handleOpenBrowser}
              className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5 h-8 text-xs"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {canRedirect ? 'Open in browser' : 'Hoe doe ik dit?'}
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={handleDismiss}
              className="h-8 text-xs text-amber-700 hover:text-amber-900 hover:bg-amber-100"
            >
              Toch doorgaan
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
